import { useCallback } from 'react';
import { useNavigate as useNavigateOriginal } from 'react-router-dom';
import type { NavigateOptions, To } from 'react-router-dom';

import { isAbsoluteUrl } from '../isAbsoluteUrl';
import { useHistory } from './useHistory';

/**
 * Возвращает императивный метод для навигации.
 *
 * Поддерживает навигацию по абсолютным URL,
 * через history.push/replace.
 *
 * Для остальных случаев используется стандартный navigate из react-router-dom.
 *
 * @see https://reactrouter.com/en/main/hooks/use-navigate
 */
export function useNavigate() {
    const navigateOriginal = useNavigateOriginal();
    const history = useHistory();

    return useCallback(
        (to: To | number, options?: NavigateOptions) => {
            // Если передано число (delta), используем оригинальную логику
            if (typeof to === 'number') {
                return navigateOriginal(to);
            }

            // Для абсолютных URL используем готовую логику из buildClientHistory
            if (typeof to === 'string' && isAbsoluteUrl(to)) {
                return options?.replace ? history.replace(to, options?.state) : history.push(to, options?.state);
            }

            // Для всего остального используем оригинальный navigate
            return navigateOriginal(to, options);
        },
        [navigateOriginal, history]
    );
}
